import type { Editor as CoreEditor } from '@ckeditor/ckeditor5-core'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import { FC, useState, useEffect } from 'react'
import { Control, Controller } from 'react-hook-form'

import FormGroup from './FormGroup'
import { Variable } from '../../common/models'
import { InsertVariableModal } from '../../home/components/settings/email-templates/InsertVariable'
import { Button, Checkbox, Textarea } from '@/components/ui'
import { cn } from '@/lib/utils'

type Props = {
  label?: string
  variables: Variable[]
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>
  name: string
  isError: boolean
  value: string
  onChange: (data: string) => void
  disabled?: boolean
  index: number
  defaultCustomHTML?: boolean
}

export const HtmlEditor: FC<Props> = ({
  label,
  name,
  variables,
  isError,
  control,
  value,
  onChange,
  disabled = false,
  index,
  defaultCustomHTML = true,
}) => {
  const [showModal, setShowModal] = useState(false)
  const [editorInstance, setEditorInstance] = useState<CoreEditor | null>(null)
  const [isCustomHTML, setIsCustomHtml] = useState(defaultCustomHTML)
  const [Editor, setEditor] = useState<any>(null)
  const [editorError, setEditorError] = useState<string | null>(null)

  useEffect(() => {
    // Skip loading if not using custom HTML
    if (!isCustomHTML) {
      setEditor(null)
      return
    }

    // Check if CKEditor is already loaded
    if ((window as any).ClassicEditor) {
      setEditor(() => (window as any).ClassicEditor)
      return
    }

    // Check if script is already in the DOM
    const existingScript = document.querySelector(
      'script[data-ckeditor-script="true"]',
    )
    if (existingScript) {
      // Wait for existing script to load
      const checkEditor = setInterval(() => {
        if ((window as any).ClassicEditor) {
          setEditor(() => (window as any).ClassicEditor)
          clearInterval(checkEditor)
        }
      }, 100)

      // Stop checking after 5 seconds
      setTimeout(() => clearInterval(checkEditor), 5000)
      return
    }

    // Load CKEditor dynamically via script tag
    const script = document.createElement('script')
    // Use absolute path from the public directory
    script.src = `${window.location.origin}/ckeditor/build/ckeditor.js`
    script.async = true
    script.setAttribute('data-ckeditor-script', 'true')

    script.onload = () => {
      if ((window as any).ClassicEditor) {
        setEditor(() => (window as any).ClassicEditor)
      } else {
        const error =
          'CKEditor script loaded but window.ClassicEditor is undefined'
        console.error(error)
        setEditorError(error)
      }
    }

    script.onerror = () => {
      const error = 'Failed to load CKEditor script'
      console.error(error)
      setEditorError(error)
    }

    document.head.appendChild(script)

    // Don't remove the script on cleanup to avoid re-loading
    return () => {
      // Reset editor state but keep the script loaded
      setEditor(null)
    }
  }, [isCustomHTML])

  return (
    <>
      <FormGroup
        label={label}
        labelWrapperClassName="align-items-center"
        addon={
          <>
            {variables.length > 0 && (
              <Button
                onClick={() => {
                  setShowModal(true)
                }}
                type="button"
                disabled={disabled}
              >
                Insert Variable
              </Button>
            )}
          </>
        }
        inputWrapperClassName="overflow-visible"
      >
        {!isCustomHTML ? (
          <Textarea
            value={value}
            onChange={(data) => {
              onChange(data.target.value)
            }}
            rows={9}
            placeholder="Enter Description"
          />
        ) : editorError ? (
          <div className="p-4 border border-destructive rounded bg-destructive/10 text-destructive">
            <p className="font-semibold">Failed to load editor</p>
            <p className="text-sm">{editorError}</p>
            <p className="text-sm mt-2">
              Try unchecking &quot;Use Rich Text Editor&quot; to use plain text
              mode.
            </p>
          </div>
        ) : Editor ? (
          <Controller
            name={name}
            control={control}
            defaultValue=""
            render={() => {
              return (
                <div
                  className={cn(
                    'long-ck-editor z-10 ck-body-wrapper w-full',
                    isError && 'border-accent-3',
                  )}
                >
                  <CKEditor<CoreEditor>
                    editor={
                      Editor as unknown as {
                        create(...args: unknown[]): Promise<CoreEditor>
                      }
                    }
                    data={value}
                    onReady={(editor) => {
                      setEditorInstance(editor)
                    }}
                    onChange={(event, editor) => {
                      const data = editor.getData()
                      onChange(data)
                    }}
                    disabled={disabled}
                  />
                </div>
              )
            }}
          />
        ) : (
          <div className="p-4 border border-monochrome-3 rounded text-monochrome-2">
            Loading editor...
          </div>
        )}
      </FormGroup>
      <FormGroup
        direction="row"
        labelClassName="whitespace-nowrap"
        inputWrapperClassName="w-auto"
        wrapperClassName="w-auto flex-row-reverse justify-end"
        label="Use Rich Text Editor (uncheck for plain text)"
      >
        <Checkbox
          className="border-monochrome-1"
          checked={isCustomHTML}
          onCheckedChange={(checked) => {
            setIsCustomHtml(!!checked)
          }}
        />
      </FormGroup>

      <InsertVariableModal
        showModal={showModal}
        setShowModal={setShowModal}
        variables={variables}
        setInsertVariable={(val) => {
          if (isCustomHTML) {
            if (editorInstance) {
              editorInstance.model.change((writer) => {
                editorInstance.model.insertContent(
                  writer.createText(val),
                  editorInstance.model.document.selection,
                )
              })
            }
          } else {
            onChange(value + val)
          }
          setShowModal(false)
        }}
      />
    </>
  )
}
